/**
 * Corporate Request Form Page for trAInedup.ai
 * Handles corporate package requests with comprehensive form
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, Calendar, MessageSquare } from 'lucide-react';
import { Layout } from '../components/common/Layout';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Card } from '../components/common/Card';
import { ROUTES } from '../utils/constants';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../services/firebase/config';

interface CorporateRequestFormData {
  // Company Information
  companyName: string;
  industry: string;
  companySize: string;
  contactName: string;
  contactRole: string;
  contactEmail: string;
  contactPhone: string;
  
  // Training Needs
  trainingGoals: string;
  targetDepartments: string[];
  currentAIUse: string;
  preferredDelivery: string;
  specificTools: string;
  
  // Logistics & Additional
  initialEmployees: string;
  futureEnrollment: string;
  trainingType: string;
  preferredStartDate: string;
  englishSpeaking: string;
  additionalRequests: string;
}

export const CorporateRequestPage: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState<CorporateRequestFormData>({
    companyName: '',
    industry: '',
    companySize: '',
    contactName: '',
    contactRole: '',
    contactEmail: '',
    contactPhone: '',
    trainingGoals: '',
    targetDepartments: [],
    currentAIUse: '',
    preferredDelivery: '',
    specificTools: '',
    initialEmployees: '',
    futureEnrollment: '',
    trainingType: '',
    preferredStartDate: '',
    englishSpeaking: '',
    additionalRequests: '',
  });

  const departmentOptions = [
    'Marketing',
    'Sales',
    'Operations',
    'Customer Service',
    'HR',
    'Leadership',
    'Technical Teams',
    'Other'
  ];

  const handleInputChange = (field: keyof CorporateRequestFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDepartmentChange = (department: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      targetDepartments: checked
        ? [...prev.targetDepartments, department]
        : prev.targetDepartments.filter(d => d !== department)
    }));
  };

  const validateForm = (): boolean => {
    const requiredFields: (keyof CorporateRequestFormData)[] = [
      'companyName',
      'industry',
      'companySize',
      'contactName',
      'contactRole',
      'contactEmail',
      'trainingGoals',
      'preferredDelivery',
      'initialEmployees',
      'trainingType',
      'preferredStartDate',
      'englishSpeaking'
    ];

    for (const field of requiredFields) {
      if (!formData[field] || (Array.isArray(formData[field]) && formData[field].length === 0)) {
        setError(`Please fill in all required fields`);
        return false;
      }
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.contactEmail)) {
      setError('Please enter a valid email address');
      return false;
    }

    if (formData.targetDepartments.length === 0) {
      setError('Please select at least one department for training');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      
      // Save to Firebase
      await addDoc(collection(db, 'corporateRequests'), {
        ...formData,
        submittedAt: new Date(),
        status: 'pending'
      });

      setIsSubmitted(true);
    } catch (err) {
      console.error('Error submitting corporate request:', err);
      
      // More detailed error handling
      if (err instanceof Error) {
        console.error('Error message:', err.message);
        console.error('Error stack:', err.stack);
        setError(`Failed to submit request: ${err.message}`);
      } else {
        console.error('Unknown error:', err);
        setError('Failed to submit request. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <Layout>
        <section className="py-20 bg-gradient-to-br from-primary-50 to-secondary-50">
          <div className="container-custom">
            <div className="max-w-2xl mx-auto text-center">
              <Card padding="lg">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <MessageSquare className="w-8 h-8 text-green-600" />
                </div>
                <h1 className="font-montserrat font-bold text-3xl text-neutral-800 mb-4">
                  Thank You for Your Request
                </h1>
                <p className="text-lg text-neutral-600 mb-6">
                  Our team will be in touch within 5 days with your proposal. In the meantime, 
                  feel free to share any additional information by emailing us at{' '}
                  <a href="mailto:info@trainedup.ai" className="text-primary hover:underline">
                    info@trainedup.ai
                  </a>.
                </p>
                <Button variant="primary" onClick={() => navigate(ROUTES.home) }>
                  Return to Home
                </Button>
              </Card>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-secondary-50 py-12">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-montserrat font-bold text-4xl md:text-5xl text-neutral-800 mb-6">
              Corporate Package Request Form
            </h1>
            <p className="text-xl text-neutral-600 mb-4">
              Tailored AI learning for your organisation
            </p>
            <p className="text-lg text-neutral-600">
              AI is transforming the way businesses operate. With the right training, your team can save 
              time, boost productivity, and unlock new opportunities. Our Corporate tier is designed for 
              organisations like yours who need a customised approach to AI learning, built entirely 
              around your goals and challenges.
            </p>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-12 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-montserrat font-bold text-3xl text-center text-neutral-800 mb-8">
              What's included
            </h2>
            <div className="grid md:grid-cols-2 gap-6 mb-12">
              <Card>
                <h3 className="font-semibold text-lg text-neutral-800 mb-3">
                  Custom training programme
                </h3>
                <p className="text-neutral-600">
                  Designed around your organisation's needs
                </p>
              </Card>
              <Card>
                <h3 className="font-semibold text-lg text-neutral-800 mb-3">
                  Flexible delivery formats
                </h3>
                <p className="text-neutral-600">
                  On-demand training packs, live workshops, or blended learning
                </p>
              </Card>
              <Card>
                <h3 className="font-semibold text-lg text-neutral-800 mb-3">
                  Expert consultations
                </h3>
                <p className="text-neutral-600">
                  To guide adoption and strategy
                </p>
              </Card>
              <Card>
                <h3 className="font-semibold text-lg text-neutral-800 mb-3">
                  Ongoing support
                </h3>
                <p className="text-neutral-600">
                  With regular updates and Q&A access
                </p>
              </Card>
            </div>

            <h2 className="font-montserrat font-bold text-3xl text-center text-neutral-800 mb-8">
              How it works
            </h2>
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <Card className="text-center">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-lg">1</span>
                </div>
                <h3 className="font-semibold text-lg text-neutral-800 mb-3">
                  Tell us about your organisation
                </h3>
                <p className="text-neutral-600">
                  Complete the request form below so we can understand your goals, team size and training needs.
                </p>
              </Card>
              <Card className="text-center">
                <div className="w-12 h-12 bg-secondary-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-lg">2</span>
                </div>
                <h3 className="font-semibold text-lg text-neutral-800 mb-3">
                  We create your tailored plan
                </h3>
                <p className="text-neutral-600">
                  Our team will design a proposal with recommended training packs, delivery format, and consultation options.
                </p>
              </Card>
              <Card className="text-center">
                <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-lg">3</span>
                </div>
                <h3 className="font-semibold text-lg text-neutral-800 mb-3">
                  Start your training journey
                </h3>
                <p className="text-neutral-600">
                  Once approved, we'll onboard your team and begin building AI skills that make a real impact.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12 bg-neutral-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <Card padding="lg">
              <form onSubmit={handleSubmit} className="space-y-8">
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-600">{error}</p>
                  </div>
                )}

                {/* Company Information */}
                <div>
                  <h3 className="font-montserrat font-bold text-2xl text-neutral-800 mb-6 flex items-center">
                    <Building2 className="w-6 h-6 mr-3 text-primary" />
                    Company Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <Input
                      label="Company name *"
                      value={formData.companyName}
                      onChange={(e) => handleInputChange('companyName', e.target.value)}
                      required
                    />
                    <Input
                      label="Industry/sector *"
                      value={formData.industry}
                      onChange={(e) => handleInputChange('industry', e.target.value)}
                      required
                    />
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Company size (number of employees) *
                      </label>
                      <select
                        value={formData.companySize}
                        onChange={(e) => handleInputChange('companySize', e.target.value)}
                        className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        required
                      >
                        <option value="">Select company size</option>
                        <option value="1-10">1-10 employees</option>
                        <option value="11-50">11-50 employees</option>
                        <option value="51-200">51-200 employees</option>
                        <option value="201-500">201-500 employees</option>
                        <option value="501-1000">501-1000 employees</option>
                        <option value="1000+">1000+ employees</option>
                      </select>
                    </div>
                    <Input
                      label="Contact name *"
                      value={formData.contactName}
                      onChange={(e) => handleInputChange('contactName', e.target.value)}
                      required
                    />
                    <Input
                      label="Contact role *"
                      value={formData.contactRole}
                      onChange={(e) => handleInputChange('contactRole', e.target.value)}
                      required
                    />
                    <Input
                      label="Contact email *"
                      type="email"
                      value={formData.contactEmail}
                      onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                      required
                    />
                    <Input
                      label="Contact phone"
                      type="tel"
                      value={formData.contactPhone}
                      onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                    />
                  </div>
                </div>

                {/* Training Needs */}
                <div>
                  <h3 className="font-montserrat font-bold text-2xl text-neutral-800 mb-6 flex items-center">
                    <Users className="w-6 h-6 mr-3 text-primary" />
                    Training Needs
                  </h3>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        What are your main goals for AI training? *
                      </label>
                      <textarea
                        value={formData.trainingGoals}
                        onChange={(e) => handleInputChange('trainingGoals', e.target.value)}
                        className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        rows={4}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-3">
                        Which teams/departments would you like training for? * (Select all that apply)
                      </label>
                      <div className="grid md:grid-cols-2 gap-3">
                        {departmentOptions.map((dept) => (
                          <label key={dept} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData.targetDepartments.includes(dept)}
                              onChange={(e) => handleDepartmentChange(dept, e.target.checked)}
                              className="mr-3 h-4 w-4 text-primary focus:ring-primary border-neutral-300 rounded"
                            />
                            <span className="text-neutral-700">{dept}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Explain current AI use
                      </label>
                      <textarea
                        value={formData.currentAIUse}
                        onChange={(e) => handleInputChange('currentAIUse', e.target.value)}
                        className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        rows={3}
                        placeholder="Describe how your organisation currently uses AI tools, if at all"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Preferred delivery *
                      </label>
                      <select
                        value={formData.preferredDelivery}
                        onChange={(e) => handleInputChange('preferredDelivery', e.target.value)}
                        className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        required
                      >
                        <option value="">Select preferred delivery method</option>
                        <option value="webinar">Webinar</option>
                        <option value="self-paced">Self-paced packs</option>
                        <option value="both">Both webinar and self-paced</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Specific AI tools you'd like training in (if you know them)
                      </label>
                      <Input
                        value={formData.specificTools}
                        onChange={(e) => handleInputChange('specificTools', e.target.value)}
                        placeholder="e.g. ChatGPT, Claude, MidJourney, Jasper, Notion AI"
                      />
                    </div>
                  </div>
                </div>

                {/* Logistics & Additional */}
                <div>
                  <h3 className="font-montserrat font-bold text-2xl text-neutral-800 mb-6 flex items-center">
                    <Calendar className="w-6 h-6 mr-3 text-primary" />
                    Logistics & Additional
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <Input
                      label="Approximate number of employees to enrol initially *"
                      type="number"
                      value={formData.initialEmployees}
                      onChange={(e) => handleInputChange('initialEmployees', e.target.value)}
                      required
                    />
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Is it likely more employees will be enrolled in the future?
                      </label>
                      <select
                        value={formData.futureEnrollment}
                        onChange={(e) => handleInputChange('futureEnrollment', e.target.value)}
                        className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="">Select option</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                        <option value="maybe">Maybe</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        One-off training or ongoing support? *
                      </label>
                      <select
                        value={formData.trainingType}
                        onChange={(e) => handleInputChange('trainingType', e.target.value)}
                        className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        required
                      >
                        <option value="">Select training type</option>
                        <option value="one-off">One-off training</option>
                        <option value="ongoing">Ongoing support</option>
                        <option value="both">Both</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Preferred start date *
                      </label>
                      <select
                        value={formData.preferredStartDate}
                        onChange={(e) => handleInputChange('preferredStartDate', e.target.value)}
                        className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        required
                      >
                        <option value="">Select preferred start date</option>
                        <option value="ASAP">ASAP</option>
                        <option value="within-1-month">Within 1 month</option>
                        <option value="within-3-months">Within 3 months</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Do the employees who will be enrolled speak English? *
                      </label>
                      <select
                        value={formData.englishSpeaking}
                        onChange={(e) => handleInputChange('englishSpeaking', e.target.value)}
                        className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        required
                      >
                        <option value="">Select option</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                        <option value="some">Some do, some don't</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Anything else you would like to request or ask
                      </label>
                      <textarea
                        value={formData.additionalRequests}
                        onChange={(e) => handleInputChange('additionalRequests', e.target.value)}
                        className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        rows={4}
                        placeholder="Any additional information, special requirements, or questions you'd like to share"
                      />
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Submitting Request...' : 'Submit Request'}
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        </div>
      </section>
    </Layout>
  );
};

